import React, { useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, List, ListItem, ListItemText, Paper, TextField, Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import type {
    NahledTyka,
    NahledTypKontrolyResponse,
} from '../../../api/JsdhNotifikaceService/generated/models/TypKontroly';

export const TEXT_NEAKTIVNI = 'Neaktivní nebo skrytá kontrola nic nenahrazuje.';
const LIMIT_SBALENI = 8;

export const textTykaSe = (n: number) => `Týká se ${n} ${n === 1 ? 'hasiče' : 'hasičů'}`;

export const textOstatni = (m: number) => {
    if (m === 1) return 'Ostatní 1 hasič ji uvidí dál';
    if (m >= 2 && m <= 4) return `Ostatní ${m} hasiči ji uvidí dál`;
    return `Ostatních ${m} hasičů ji uvidí dál`;
};

export const textNahrazenaJinou = (n: number, nazev: string) =>
    `${n} ${n === 1 ? 'hasiči' : 'hasičům'} je tato kontrola nahrazena kontrolou ${nazev}`;

/** Stav po ulozeni: plati do / chybi / prevzato z (spec 6.1 bod 3). */
export const popisStavu = (t: NahledTyka): string => {
    const datum = t.datumExpirace ? dayjs(t.datumExpirace).format('DD.MM.YYYY') : '';
    let text: string;
    switch (t.status) {
        case 'PLATNA':
            text = `platí do ${datum}`;
            break;
        case 'BLIZI_SE_EXPIRACE':
            text = `vyprší ${datum}`;
            break;
        case 'EXPIROVANA':
            text = `expirovala ${datum}`;
            break;
        default:
            text = 'chybí';
    }
    return t.prevzatoZTypu ? `${text}, převzato z: ${t.prevzatoZTypu.nazev}` : text;
};

interface PolozkaSeznamu {
    klic: number;
    jmeno: string;
    popis?: string;
}

/** Seznam jmen; nad 8 polozek sbaleny a s vyhledavanim podle jmena. */
export const SeznamJmen: React.FC<{ polozky: PolozkaSeznamu[] }> = ({ polozky }) => {
    const [rozbaleno, setRozbaleno] = useState(false);
    const [hledat, setHledat] = useState('');
    const dlouhy = polozky.length > LIMIT_SBALENI;
    const filtr = hledat.trim().toLowerCase();
    const filtrovane = filtr ? polozky.filter((p) => p.jmeno.toLowerCase().includes(filtr)) : polozky;
    const zobrazene = dlouhy && !rozbaleno && !filtr ? filtrovane.slice(0, LIMIT_SBALENI) : filtrovane;

    return (
        <Box>
            {dlouhy && (
                <TextField
                    size="small"
                    placeholder="Hledat podle jména"
                    value={hledat}
                    onChange={(e) => setHledat(e.target.value)}
                    fullWidth
                    sx={{ my: 0.5 }}
                />
            )}
            <List dense disablePadding>
                {zobrazene.map((p) => (
                    <ListItem key={p.klic} disableGutters sx={{ py: 0 }}>
                        <ListItemText primary={p.jmeno} secondary={p.popis} />
                    </ListItem>
                ))}
            </List>
            {dlouhy && !filtr && (
                <Button size="small" onClick={() => setRozbaleno(!rozbaleno)}>
                    {rozbaleno ? 'Sbalit' : `Zobrazit všech ${polozky.length}`}
                </Button>
            )}
        </Box>
    );
};

interface Props {
    data?: NahledTypKontrolyResponse;
    isLoading: boolean;
    isError: boolean;
    /** Data jsou z predchoziho requestu (keepPreviousData) - obsah se ztlumi, dokud nedorazi aktualni. */
    isPlaceholder?: boolean;
}

/** Panel „Koho se kontrola týká“ - zivy nahled dopadu (spec 6.1 bod 3). */
const NahledTypKontrolyPanel: React.FC<Props> = ({ data, isLoading, isError, isPlaceholder = false }) => (
    <Paper
        variant="outlined"
        sx={{ p: 2, mt: 2 }}
        aria-busy={isPlaceholder}
        data-testid="nahled-typ-kontroly-panel"
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Koho se kontrola týká</Typography>
            {isPlaceholder && <CircularProgress size={14} aria-label="Načítání náhledu" />}
        </Box>
        {isLoading && !data && <CircularProgress size={20} />}
        <Box sx={{ opacity: isPlaceholder ? 0.5 : 1, transition: 'opacity 0.15s' }} data-testid="nahled-obsah">
        {isError && <Alert severity="error">Náhled se nepodařilo načíst.</Alert>}
        {data && (
            <>
                {data.chyby.map((chyba) => (
                    <Alert key={chyba} severity="error" sx={{ mb: 1 }}>{chyba}</Alert>
                ))}
                {data.neaktivni && <Alert severity="info" sx={{ mb: 1 }}>{TEXT_NEAKTIVNI}</Alert>}

                <Typography variant="body2" fontWeight={600}>{textTykaSe(data.tyka.length)}</Typography>
                <SeznamJmen polozky={data.tyka.map((t) => ({
                    klic: t.uzivatelId, jmeno: t.jmeno, popis: `${t.duvod}: ${popisStavu(t)}`,
                }))} />

                {data.nahrazuje.map((n) => {
                    const tykaIds = new Set(data.tyka.map((t) => t.uzivatelId));
                    const shodne = n.skrytoUzivatelum.length > 0
                        && n.skrytoUzivatelum.length === tykaIds.size
                        && n.skrytoUzivatelum.every((u) => tykaIds.has(u.uzivatelId));
                    return (
                        <Box key={`nahrazuje-${n.typId}`} sx={{ mt: 1.5 }}>
                            {shodne ? (
                                <Typography variant="body2" fontWeight={600}>
                                    {`Všem výše uvedeným se skryje: ${n.nazev}`}
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="body2" fontWeight={600}>
                                        {`Těmto hasičům se skryje: ${n.nazev} (${n.skrytoUzivatelum.length})`}
                                    </Typography>
                                    <SeznamJmen polozky={n.skrytoUzivatelum.map((u) => ({ klic: u.uzivatelId, jmeno: u.jmeno }))} />
                                </>
                            )}
                            <Typography variant="body2" color="text.secondary">{textOstatni(n.zustavaUzivatelum)}</Typography>
                        </Box>
                    );
                })}

                {data.nahrazenoJinymi.map((n) => (
                    <Box key={`nahrazeno-${n.typId}`} sx={{ mt: 1.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                            {textNahrazenaJinou(n.uzivatele.length, n.nazev)}
                        </Typography>
                        <SeznamJmen polozky={n.uzivatele.map((u) => ({ klic: u.uzivatelId, jmeno: u.jmeno }))} />
                    </Box>
                ))}

                {data.vicNahrazujicich.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Tito hasiči spadají pod víc kontrol, které nahrazují stejnou kontrolu, a uvidí všechny:
                        {data.vicNahrazujicich.map((v) => (
                            <Box key={v.uzivatelId}>{`${v.jmeno}: ${v.typy.map((t) => t.nazev).join(', ')}`}</Box>
                        ))}
                    </Alert>
                )}
            </>
        )}
        </Box>
    </Paper>
);

export default NahledTypKontrolyPanel;
